import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert, InputGroup } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import ImageCropper from '../Utils/ImageCropper';
import PhoneInput from '../Input/PhoneInput';
import { useProvider, Toast } from '../../context/MenuProvider';

const ApnForm = () => {
  const { id, prospecto, retorno } = useParams();
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const [downlines, setDownlines] = useState([]);
  const [prospectos, setProspectos] = useState([]);
  const [apn, setApn] = useState(null);
  const [paraDownline, setParaDownline] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const {
    crudAdmin,
    userAdmin,
    salvarDados,
    setLinks,
    setBtns,
  } = useProvider();
  const { post, del, patch, put, get, loading } = crudAdmin || {};

  const location = useLocation();

  useEffect(() => {
    if (id) {
      carregarApn();
    } else {
      setApn((item) => ({
        ...item,
        data:new Date().toLocaleDateString('sv-SE'),
      }))
    }
  }, [id]);

  useEffect(() => {
    if (query && paraDownline) buscarDownlines();
  }, [query]);
  
  useEffect(() => {
    if (apn?.nome || prospecto) buscarProspectos();
  }, [apn?.nome, prospecto]);

  useEffect(() => {
    setLinks([]);
    setBtns([
      ...(id ?[{
        icon: 'bi-trash',
        onClick: async (e) => {
          e?.preventDefault();
          await handleDelete();
        },
      }]:[]),
      {
        icon: 'bi-check-lg',
        onClick: async (e) => {
          e?.preventDefault();
          await handleSubmit();
        },
      },
    ]);

    return () => {
      setLinks([]);
      setBtns([]);
    };
  }, [setLinks, setBtns, apn]);

  const carregarApn = async () => {
    try {
      const response = await get(`/rede/apn/${id}/?expand=upline,prospecto`, 'pagina');
      if (response && response.length) {
        const dados = response[0];
        if (dados?.upline) {
          setDownlines([dados?.upline]);
          setParaDownline(true);
        }
        if (dados?.prospecto) {
          setProspectos([dados?.prospecto]);
        }
        setApn(dados);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const buscarDownlines = async () => {
    try {
      const response = await get(`/rede/consultores/?expand=upline&search=${query}`, 'upline');
      if (response && response.length) {
        setDownlines(prev => {
          const itemAtualId = apn?.upline?.id;
          let novosItens = response[0].results.filter(item => item.id != null);

          // Garante que o upline atual esteja na lista
          if (itemAtualId && !novosItens.some(item => item.id === itemAtualId)) {
            const atual = prev.find(item => item.id === itemAtualId);
            if (atual) novosItens.push(atual);
          }

          return novosItens;
        });
      }
    } catch (err) {
      console.error(err);
    }
  };
  
  const buscarProspectos = async () => {
    try {
      const response = await get(`/rede/prospectos/?ordering=contato__nome${(prospecto)?`&ids=${prospecto}`:''}${apn?.nome?`&search=${apn?.nome}`:''}`, 'prospectos');
      if (response && response.length) {
        setProspectos(prev => {
          const itemAtualId = apn?.prospecto?.id;
          let novosItens = response[0].results.filter(item => item.id != null);

          // Garante que o upline atual esteja na lista
          if (itemAtualId && !novosItens.some(item => item.id === itemAtualId)) {
            const atual = prev.find(item => item.id === itemAtualId);
            if (atual) novosItens.push(atual);
          }

          return novosItens;
        });
        if(prospecto){
            const item = response[0].results.find(item => parseInt(item.id) === parseInt(prospecto));
            setApn((prev)=>({
              ...prev,
              prospecto:item
            }))
        }
      }
    } catch (err) {
      console.error(err);
    }
  };


  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const errors = {};
    if (!apn?.data || apn.data === '') errors.data = 'Por favor, informe a data da apresentação.';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    const formData = new FormData();
    formData.append('data', apn?.data);
    formData.append('upline', apn?.upline?.id || parseInt(apn?.upline) || ``);
    formData.append('prospecto', apn?.prospecto?.id || parseInt(apn?.prospecto) || ``);
    formData.append('observacoes', apn?.observacoes || ``);

    
    if(!id){
      Swal.fire({
        title: "Fez pré cadastro?",
        text: `${apn?.prospecto?.nome || ``} fez o pré cadastro?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim!",
        cancelButtonText: "Não"
      }).then(async(result) => {
        formData.append('pre_cadastro', !!result.isConfirmed);
        await salvarApn(formData);
      });
    } else {
        formData.append('pre_cadastro', !!apn?.pre_cadastro);
        await salvarApn(formData);
    } 
  };
  
  const handleDelete = async (e) => {
    if (e) e.preventDefault();
    
    Swal.fire({
        title: "Excluir APN?",
        text: `Excluir definitivamente a APN realizada com ${apn?.prospecto?.nome || ``}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim!",
        cancelButtonText: "Não"
    }).then(async(result) => {
        if(result.isConfirmed){
          try{
            await del(`/rede/apn/${id}/`);
            Toast.fire({
              icon: 'success',
              title: 'APN excluída com sucesso',
            });
            navigate(-1);
          }catch(err){
            Toast.fire({
              icon: 'warning',
              title: 'Falha ao excluir APN',
            });
          }finally{
            
          }
        }
    });
  };
  
  const salvarApn = async (formData) => {
    try {
      const apiRequest = id ? patch : post;
      const response = await apiRequest(
        formData,
        `/rede/apn/${id || ''}${id ? '/' : ''}?filtros=id`,
        'multipart/form-data',
        'salvar'
      );

      if (response && response.length) {
        Toast.fire({
          icon: 'success',
          title: 'Apn salva com sucesso',
        });
        switch(retorno){
          case `prospectos`:
            navigate(`/rede/prospectos/acompanhar/`,{replace:true});
            break;
          default:
            navigate(-1);
            break;
        }
      }
    } catch (err) {
      const errorMessage =
        typeof err.response?.data === 'string'
          ? 'Erro ao salvar Apn.'
          : err.response?.data?.detail;

      setFormErrors(prev => ({
        ...prev,
        apiError: errorMessage,
        telefone: err.response?.data?.telefone,
        documento: err.response?.data?.cpf_cnpj,
        nome: err.response?.data?.nome,
        data: err.response?.data?.data,
        endereco: err.response?.data?.endereco,
        diaDoAniversario: err.response?.data?.dia_do_aniversario,
        ativo: err.response?.data?.ativo,
        observacoes: err.response?.data?.observacoes,
        crediario: err.response?.data?.crediario,
        regiao: err.response?.data?.regiao,
      }));
    }
  }
  
  const carregarMais = () => {
    if(!paraDownline){
      setApn((prev) => ({
        ...prev,
        upline:null
      }))
    }
    setParaDownline(!paraDownline);
  }
  
  // render recursivo
  const renderDownlines = (lista, todas, nivel = 0) => {
    return lista.map(item => {
      const filhos = todas.filter(c => c.upline?.id === item.id);

      return (
        <React.Fragment key={item.id}>
          <option value={item.id}>
            {`${'— '.repeat(nivel)}${item?.nome || ''}`}
          </option>
          {renderDownlines(filhos, todas, nivel + 1)}
        </React.Fragment>
      );
    });
  };


  return (
    <div>
      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            {formErrors.apiError && <Alert variant="danger">{formErrors.apiError}</Alert>}
            {paraDownline ? (<>
              <Form.Group controlId="formPesquisaDownline" className="mb-3">
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Pesquisar downline..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <Button variant="secondary" onClick={() => buscarDownlines()}>
                    <i className="bi bi-search"></i>
                  </Button>
                  <Button
                    size="sm" 
                    onClick={carregarMais}
                    variant="outline-secondary"
                    className="px-3"
                  ><i className="bx bx-chevron-up"></i></Button>
                </InputGroup>
              </Form.Group>
  
              <Form.Group controlId="formUpline" className="mt-3 pb-3 border-dashed-bottom">
                <InputGroup>
                  <span className="input-group-text">Downline</span>
                  <Form.Control
                    as="select"
                    value={apn?.upline?.id || ''}
                    onChange={(e) =>
                      setApn((item) => ({
                        ...item,
                        upline: {
                          ...item.upline,
                          id:e.target.value,
                        }
                      }))
                    }
                  >
                    <option value=""></option>
                    {renderDownlines(downlines.filter(c => !c.upline), downlines)}
                  </Form.Control>
                </InputGroup>
              </Form.Group>
            </>) : (<>
              <div className="text-center">
                <Button
                  size="sm" 
                  onClick={carregarMais}
                  variant="outline-secondary"
                  className="px-5"
                >Para Rede <i className="bx bx-chevron-down"></i></Button>
              </div>
            </>)}
            {(!prospecto && !id) && (
              <Form.Group controlId="formNomeDoConsultor" className="mt-3">
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Pesquisar prospectos..."
                    value={apn?.nome}
                    onChange={(e) => setApn((prev) => ({ ...prev, nome: e.target.value }))}
                  />
                  <Button variant="secondary" onClick={() => buscarDownlines()}>
                    <i className="bi bi-search"></i>
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate(`/rede/prospectos/contatos/`)}>
                    <i className="bi bi-plus"></i>
                  </Button>
                </InputGroup>
              </Form.Group>
            )}
            <Form.Group controlId="formContato" className="mt-3">
                <InputGroup>
                  <span className="input-group-text">Prospecto</span>
                  <Form.Control
                    as="select"
                    disabled={!!prospecto || !!id}
                    value={apn?.prospecto?.id || parseInt(apn?.prospecto) || prospecto || ''}
                    onChange={(e) =>
                      setApn((item) => ({
                        ...item,
                        prospecto: {
                          ...item?.prospecto, // evita erro se contato for undefined
                          id: e.target.value,
                        },
                      }))
                    }
                  >
                    <option value=""></option>
                    {prospectos.map(item => (
                      <option key={item.id} value={item.id}>
                        {item?.nome}
                      </option>
                    ))}
                  </Form.Control>
                </InputGroup>
                {formErrors.contato && <Alert variant="danger">{formErrors.nome}</Alert>}
            </Form.Group>

            <Form.Group controlId="formDataCadastro" className="mt-3">
              <InputGroup>
                <span className="input-group-text">Data</span>
                <Form.Control
                  type="date"
                  value={apn?.data || ''}
                  onChange={(e) =>
                    setApn((item) => ({
                      ...item,
                      data: e.target.value,
                    }))
                  }
                />
              </InputGroup>
              {formErrors.data && <Alert variant="danger">{formErrors.data}</Alert>}
            </Form.Group>
            <Form.Group controlId="formObservacoes" className="mt-3">
              <Form.Control
                as="textarea"
                value={apn?.observacoes}
                onChange={(e) => setApn((prev)=> ({
                  ...prev,
                  observacoes:e.target.value
                }))}
                placeholder="Observações"
                rows={5}
                style={{ resize: 'none' }}
              />
            </Form.Group>
            {id && (<>
              <Form.Group controlId="formPreCadastro" className="mt-2">
                <Form.Check
                  type="switch"
                  label="Fez o pré-cadastro na apresentação?"
                  checked={apn?.pre_cadastro}
                  onChange={(e) =>
                    setApn((prev) => ({
                      ...prev,
                      pre_cadastro: e.target.checked,
                    }))
                  }
                />
                {formErrors.pre_cadastro && (
                  <Alert variant="danger">{formErrors.pre_cadastro}</Alert>
                )}
              </Form.Group>
            </>)}
            <Button variant="secondary" type="submit" className="mt-4 w-100" disabled={loading}>
              Salvar
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ApnForm;

